// src/pages/Customers/CustomersPage.tsx
import React, {useCallback, useState} from "react";
import {
  Users,
  UserCheck,
  UserX,
  Plus,
  Eye,
  Edit,
  Trash2,
  Mail,
  Phone,
  Calendar,
} from "lucide-react";
import {useTable} from "../../hooks/common/useTable";
import {useToast} from "../../hooks/common/useToast";
import type {UserFilterDto, UserResponseDto} from "../../types/users";
import {
  LoadingSpinner,
  PageHeader,
  type DataTableBulkAction,
  type DataTableColumn,
} from "../../components";
import {
  createCommonBulkActions,
  DataTable,
} from "../../components/common/DataTable";
import {useCustomers} from "../../hooks/useCustomer";
import StatsCard from "../../components/ui/StatsCard";
import CustomerFormModal from "./components/CustomerFormModal";
import CustomerDetailModal from "./components/CustomerDetailModal";

const CustomersPage: React.FC = () => {
  const {success} = useToast();
  const [selectedCustomer, setSelectedCustomer] =
    useState<UserResponseDto | null>(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Table state management
  const {
    currentPage,
    pageSize,
    handlePageChange,
    handleFilterChange,
    handleSort,
    getQueryParams,
  } = useTable({
    key: "customers",
    defaultPageSize: 25,
  });

  // Build query parameters for API (only customers - USER role)
  const queryParams = {...getQueryParams(), role: "USER"} as UserFilterDto;

  // Use the customers hook with table parameters
  const {
    customers,
    pagination,
    stats,
    isLoading,
    handleUpdate,
    handleDelete,
    handleVerifyEmail,
    handleBulkDelete,
    refetch,
  } = useCustomers(queryParams);

  // Event handlers

  const handleEditCustomer = useCallback((customer: UserResponseDto) => {
    setSelectedCustomer(customer);
    setIsEditMode(true);
    setIsFormModalOpen(true);
  }, []);

  const handleViewCustomer = useCallback((customer: UserResponseDto) => {
    setSelectedCustomer(customer);
    setIsDetailModalOpen(true);
  }, []);

  const handleDeleteCustomer = useCallback(
    async (customerId: number) => {
      const result = await handleDelete(customerId);
      if (result) {
        success("Success", "Customer deleted successfully");
        refetch();
      }
    },
    [handleDelete, success, refetch]
  );

  const handleVerifyCustomer = useCallback(
    async (customerId: number) => {
      const result = await handleVerifyEmail(customerId);
      if (result) {
        success("Success", "Customer email verified successfully");
        refetch();
      }
    },
    [handleVerifyEmail, success, refetch]
  );

  const handleBulkDeleteCustomers = useCallback(
    async (customerIds: number[]) => {
      const result = await handleBulkDelete(customerIds);
      if (result) {
        success(
          "Success",
          `${customerIds.length} customers deleted successfully`
        );
        refetch();
      }
    },
    [handleBulkDelete, success, refetch]
  );

  // Table columns definition
  const columns: DataTableColumn<UserResponseDto>[] = [
    {
      key: "name",
      title: "Customer Name",
      sortable: true,
      render: (_value, customer: UserResponseDto) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            {customer.avatar ? (
              <img
                className="h-8 w-8 rounded-full"
                src={customer.avatar}
                alt={customer.name}
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                <Users className="h-4 w-4 text-gray-600" />
              </div>
            )}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {customer.name}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              ID: {customer.id}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "email",
      title: "Email",
      sortable: true,
      render: (_value, customer: UserResponseDto) => (
        <div className="flex items-center space-x-2">
          <Mail className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-900 dark:text-gray-100">
            {customer.email}
          </span>
          {customer.isVerified && (
            <UserCheck className="h-4 w-4 text-green-500" />
          )}
        </div>
      ),
    },
    {
      key: "phone",
      title: "Phone",
      render: (_value, customer: UserResponseDto) =>
        customer.phone ? (
          <div className="flex items-center space-x-2">
            <Phone className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-900 dark:text-gray-100">
              {customer.phone}
            </span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        ),
    },
    {
      key: "isVerified",
      title: "Status",
      sortable: true,
      render: (_value, customer: UserResponseDto) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            customer.isVerified
              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
          }`}
        >
          {customer.isVerified ? "Verified" : "Pending"}
        </span>
      ),
    },
    {
      key: "lastLogin",
      title: "Last Login",
      sortable: true,
      render: (_value, customer: UserResponseDto) =>
        customer.lastLogin ? (
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-900 dark:text-gray-100">
              {new Date(customer.lastLogin).toLocaleDateString()}
            </span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">Never</span>
        ),
    },
    {
      key: "propertyCount",
      title: "Properties",
      render: (_value, customer: UserResponseDto) => (
        <span className="text-sm text-gray-900 dark:text-gray-100">
          {customer.propertyCount || 0}
        </span>
      ),
    },
    {
      key: "createdAt",
      title: "Joined",
      sortable: true,
      render: (_value, customer: UserResponseDto) => (
        <span className="text-sm text-gray-900 dark:text-gray-100">
          {new Date(customer.createdAt).toLocaleDateString()}
        </span>
      ),
    },
  ];

  // Row actions
  const actions = [
    {
      label: "View Details",
      icon: <Eye className="w-4 h-4" />,
      onClick: handleViewCustomer,
    },
    {
      label: "Edit",
      icon: <Edit className="w-4 h-4" />,
      onClick: handleEditCustomer,
    },
    {
      label: "Verify Email",
      icon: <UserCheck className="w-4 h-4" />,
      onClick: (customer: UserResponseDto) => handleVerifyCustomer(customer.id),
      show: (customer: UserResponseDto) => !customer.isVerified,
    },
    {
      label: "Delete",
      icon: <Trash2 className="w-4 h-4" />,
      onClick: (customer: UserResponseDto) => handleDeleteCustomer(customer.id),
      variant: "danger" as const,
    },
  ];

  // Bulk actions
  const commonBulkActions = createCommonBulkActions<UserResponseDto>();
  const bulkActions: DataTableBulkAction<UserResponseDto>[] = [
    commonBulkActions.bulkDelete((customers) =>
      handleBulkDeleteCustomers(customers.map((customer) => customer.id))
    ),
  ];

  // Filter options
  const filters = [
    {
      key: "search",
      label: "Search",
      type: "text" as const,
      placeholder: "Search customers by name or email...",
    },
    {
      key: "isVerified",
      label: "Verification Status",
      type: "select" as const,
      options: [
        {label: "All Customers", value: ""},
        {label: "Verified", value: "true"},
        {label: "Unverified", value: "false"},
      ],
    },
  ];

  if (isLoading && !customers.length) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Customer Management"
        // action={{
        //   label: "Add Customer",
        //   onClick: handleCreateCustomer,
        //   icon: <Plus className="w-4 h-4" />,
        // }}
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard
            title="Total Customers"
            value={stats.regularUsers}
            icon={<Users className="w-6 h-6" />}
            color="bg-blue-100 text-blue-600"
          />
          <StatsCard
            title="Verified Customers"
            value={stats.verifiedUsers}
            icon={<UserCheck className="w-6 h-6" />}
            color="bg-green-100 text-green-600"
          />
          <StatsCard
            title="Unverified Customers"
            value={stats.unverifiedUsers}
            icon={<UserX className="w-6 h-6" />}
            color="bg-yellow-100 text-yellow-600"
          />
          <StatsCard
            title="Active This Month"
            value={stats.activeUsers}
            icon={<Calendar className="w-6 h-6" />}
            color="bg-purple-100 text-purple-600"
          />
        </div>
      )}

      {/* Data Table */}
      <DataTable<UserResponseDto>
        data={customers}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize,
          total: pagination?.total || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: true,
          onChange: handlePageChange,
        }}
        sortable
        onSort={(sort) => {
          if (sort) {
            handleSort(sort.field);
          }
        }}
        filters={filters}
        onFilter={(filters) => {
          Object.entries(filters).forEach(([key, value]) => {
            handleFilterChange(key, value);
          });
        }}
        searchable
        searchPlaceholder="Search customers by name or email..."
        onSearch={(value) => handleFilterChange("search", value)}
        actions={actions}
        bulkActions={bulkActions}
        emptyText="No customers found"
        emptyIcon={<Users className="w-12 h-12" />}
      />

      {/* Modals */}
      <CustomerFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        customer={selectedCustomer}
        isEditMode={isEditMode}
      />

      <CustomerDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        customer={selectedCustomer}
      />
    </div>
  );
};

export default CustomersPage;
